/* ============================================
   YOUTUBE EMBED COMPONENT
   The Creative Orchestra - GEMINI Framework
   Responsive YouTube video embed
   ============================================ */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Video } from 'lucide-react';
import { useTheme } from '@context/ThemeContext';

/**
 * Extract YouTube video ID from various URL formats
 */
const getYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  return match ? match[1] : null;
};

/**
 * YouTube Embed Component
 * Responsive embedded YouTube player with lazy loading
 *
 * @param {Object} props
 * @param {string} props.youtubeUrl - YouTube video URL
 * @param {string} props.workName - Name of the work for labeling
 */
const YouTubeEmbed = ({ youtubeUrl, workName, className = '' }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { activeTheme } = useTheme();

  const accentColor = activeTheme?.colors?.accent || '#818CF8';
  const accentRgb =
    activeTheme?.cssVariables?.['--theme-accent-rgb'] || '129, 140, 248';

  const videoId = getYouTubeId(youtubeUrl);
  if (!videoId) return null;

  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;

  return (
    <section className={`py-12 px-4 ${className}`}>
      <div className="max-w-4xl mx-auto">
        <motion.div
          className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          {/* Header */}
          <div className="p-6 pb-4 flex items-center gap-3">
            <div
              className="p-2.5 rounded-xl"
              style={{ background: `rgba(${accentRgb}, 0.15)` }}
            >
              <Video className="w-5 h-5" style={{ color: accentColor }} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                Watch the Video
              </h3>
              <p className="text-sm text-gray-400">{workName} — Video Overview</p>
            </div>
          </div>

          {/* Video container with 16:9 aspect ratio */}
          <div className="px-6 pb-6">
            <div className="relative w-full rounded-xl overflow-hidden" style={{ paddingTop: '56.25%' }}>
              {!isLoaded ? (
                <motion.button
                  className="absolute inset-0 w-full h-full cursor-pointer group"
                  onClick={() => setIsLoaded(true)}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  {/* Thumbnail */}
                  <img
                    src={thumbnailUrl}
                    alt={`${workName} video thumbnail`}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />

                  {/* Dark overlay */}
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors" />

                  {/* Play button */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <motion.div
                      className="w-20 h-20 rounded-full flex items-center justify-center shadow-2xl"
                      style={{
                        background: `rgba(${accentRgb}, 0.85)`,
                        boxShadow: `0 0 40px rgba(${accentRgb}, 0.4)`,
                      }}
                      whileHover={{ scale: 1.1 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      <Play className="w-8 h-8 text-white ml-1" fill="white" />
                    </motion.div>
                  </div>

                  {/* Click to play text */}
                  <div className="absolute bottom-4 left-0 right-0 text-center">
                    <span className="text-sm text-white/70 bg-black/40 px-3 py-1 rounded-full">
                      Click to play
                    </span>
                  </div>
                </motion.button>
              ) : (
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={embedUrl}
                  title={`${workName} video`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default YouTubeEmbed;
